import { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtINR, fmtDate } from "@/utils/format";
import { toast } from "sonner";
import { ArrowLeft, Printer, Plus, Trash2, HandCoins, Undo2, Gift } from "lucide-react";
import { Mini } from "./AdminDailyReports";
import { NetBadge } from "./AdminCash";

const istToday = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
const monthStart = () => istToday().slice(0, 8) + "01";

const TYPE_META = {
  shortage: { cls: "text-red-600", tag: "Shortage" },
  excess: { cls: "text-emerald-700", tag: "Excess" },
  advance: { cls: "text-red-600", tag: "Advance" },
  return: { cls: "text-emerald-700", tag: "Returned" },
  discount: { cls: "text-blue-700", tag: "Discount" },
};

export function LedgerTable({ data, onDelete, testId = "staff-ledger-table" }) {
  return (
    <table className="w-full text-sm" data-testid={testId}>
      <thead className="bg-slate-50 text-[11px] uppercase text-slate-500 text-left">
        <tr><th className="py-2.5 px-3">Date</th><th className="py-2.5 px-3">Particulars</th><th className="py-2.5 px-3">Mode</th><th className="py-2.5 px-3 text-right">Dr (staff owes)</th><th className="py-2.5 px-3 text-right">Cr (to staff)</th><th className="py-2.5 px-3 text-right">Balance</th>{onDelete && <th />}</tr>
      </thead>
      <tbody>
        <tr className="border-t border-slate-100 bg-slate-50/60" data-testid="ledger-opening"><td className="py-2 px-3" colSpan={5}>Opening balance (carried forward)</td><td className="py-2 px-3 text-right"><NetBadge v={data.opening_balance} /></td>{onDelete && <td />}</tr>
        {data.rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-500">No entries in this period.</td></tr>}
        {data.rows.map((r) => {
          const m = TYPE_META[r.type];
          return (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`ledger-row-${r.id}`}>
              <td className="py-2 px-3 whitespace-nowrap">{fmtDate(r.date)}</td>
              <td className="py-2 px-3"><span className={`text-[10px] uppercase font-semibold mr-2 ${m.cls}`}>{m.tag}</span>{r.label}{r.note && <div className="text-[11px] text-slate-500">{r.note}</div>}</td>
              <td className="py-2 px-3 uppercase text-xs text-slate-500">{r.mode || "—"}</td>
              <td className="py-2 px-3 text-right font-mono-num text-red-600">{r.amount < 0 ? fmtINR(-r.amount) : ""}</td>
              <td className="py-2 px-3 text-right font-mono-num text-emerald-700">{r.amount > 0 ? fmtINR(r.amount) : ""}</td>
              <td className="py-2 px-3 text-right"><NetBadge v={r.balance} /></td>
              {onDelete && <td className="py-2 px-2 text-right">{r.kind !== "shift" && <button onClick={() => onDelete(r)} className="text-slate-400 hover:text-red-600 print:hidden" data-testid={`ledger-del-${r.id}`}><Trash2 className="w-4 h-4" /></button>}</td>}
            </tr>
          );
        })}
        <tr className="border-t-2 border-slate-300 font-semibold" data-testid="ledger-closing"><td className="py-2.5 px-3" colSpan={5}>Closing balance</td><td className="py-2.5 px-3 text-right"><NetBadge v={data.closing_balance} testId="ledger-closing-balance" /></td>{onDelete && <td />}</tr>
      </tbody>
    </table>
  );
}

export function LedgerTotals({ t }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ledger-totals">
      <Mini label="Shortage" value={fmtINR(t.shortage)} />
      <Mini label="Excess" value={fmtINR(t.excess)} green />
      <Mini label="Advance given" value={fmtINR(t.advance)} />
      <Mini label="Returned" value={fmtINR(t.returned)} green />
      <Mini label="Discount" value={fmtINR(t.discount)} blue />
    </div>
  );
}

export function StaffLedgers() {
  const [list, setList] = useState(null);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get("/admin/staff-ledger").then((r) => setList(r.data)).catch((e) => toast.error(formatError(e))); }, [sel]);
  if (sel) return <StaffLedgerDetail emp={sel} onBack={() => setSel(null)} />;
  return (
    <>
      {list && <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5" data-testid="staff-ledger-summary">
        <Mini label="Staff" value={list.rows.length} />
        <Mini label="Owed by staff" value={fmtINR(list.rows.filter((r) => r.balance < 0).reduce((a, r) => a - r.balance, 0))} />
        <Mini label="Payable to staff" value={fmtINR(list.rows.filter((r) => r.balance > 0).reduce((a, r) => a + r.balance, 0))} green />
      </div>}
      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="staff-ledger-list">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500 text-left">
              <tr><th className="py-3 px-3">Employee</th><th className="py-3 px-3 text-right">Shortage</th><th className="py-3 px-3 text-right">Excess</th><th className="py-3 px-3 text-right">Advance</th><th className="py-3 px-3 text-right">Returned</th><th className="py-3 px-3 text-right">Discount</th><th className="py-3 px-3 text-right">Balance</th><th className="py-3 px-3">Last entry</th><th /></tr>
            </thead>
            <tbody>
              {!list && <tr><td colSpan={9} className="py-8 text-center text-slate-500">Loading…</td></tr>}
              {list?.rows.map((r) => (
                <tr key={r.employee_id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setSel(r)} data-testid={`staff-row-${r.employee_code}`}>
                  <td className="py-2.5 px-3 font-medium whitespace-nowrap">{r.employee_name} <span className="text-slate-400 font-normal">({r.employee_code})</span>{!r.active && <span className="ml-2 text-[10px] uppercase text-slate-400">inactive</span>}</td>
                  <td className="py-2.5 px-3 text-right font-mono-num text-red-600">{fmtINR(r.shortage)}</td>
                  <td className="py-2.5 px-3 text-right font-mono-num text-emerald-700">{fmtINR(r.excess)}</td>
                  <td className="py-2.5 px-3 text-right font-mono-num text-red-600">{fmtINR(r.advance)}</td>
                  <td className="py-2.5 px-3 text-right font-mono-num text-emerald-700">{fmtINR(r.returned)}</td>
                  <td className="py-2.5 px-3 text-right font-mono-num">{fmtINR(r.discount)}</td>
                  <td className="py-2.5 px-3 text-right"><NetBadge v={r.balance} testId={`balance-${r.employee_code}`} /></td>
                  <td className="py-2.5 px-3 text-xs text-slate-500 whitespace-nowrap">{r.last_date ? fmtDate(r.last_date) : "—"}</td>
                  <td className="py-2.5 px-3 text-right"><Button size="sm" variant="outline" className="rounded-full h-8" data-testid={`open-ledger-${r.employee_code}`}>Ledger</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}

function StaffLedgerDetail({ emp, onBack }) {
  const { user } = useAuth();
  const [range, setRange] = useState({ from_date: monthStart(), to_date: istToday() });
  const [data, setData] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState({ amount: "", mode: "cash", date: istToday(), note: "" });

  const load = () => api.get(`/admin/staff-ledger/${emp.employee_id}`, { params: range }).then((r) => setData(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range]);

  const open = (type) => { setForm({ amount: "", mode: "cash", date: istToday(), note: "" }); setDlg(type); };
  const save = async () => {
    try {
      await api.post("/admin/staff-ledger", { employee_id: emp.employee_id, type: dlg, amount: Number(form.amount), mode: dlg === "discount" ? null : form.mode, date: form.date, note: form.note || null });
      toast.success("Entry saved"); setDlg(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.label}" ${fmtINR(Math.abs(r.amount))}?`)) return;
    try { await api.delete(`/admin/staff-ledger/${r.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div data-testid="staff-ledger-detail">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4 print:hidden">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" className="rounded-full" onClick={onBack} data-testid="ledger-back-btn"><ArrowLeft className="w-4 h-4 mr-1" /> All staff</Button>
          <div><div className="text-xl font-display font-bold">{emp.employee_name} <span className="text-slate-400 font-normal text-sm">({emp.employee_code})</span></div><div className="text-xs text-slate-500">Continuous ledger · balance carries forward</div></div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" className="rounded-full bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]" onClick={() => open("advance")} data-testid="add-advance-btn"><HandCoins className="w-4 h-4 mr-1" /> Advance given</Button>
          <Button size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => open("return")} data-testid="add-return-btn"><Undo2 className="w-4 h-4 mr-1" /> Money returned</Button>
          {user?.role === "admin" && <Button size="sm" variant="outline" className="rounded-full" onClick={() => open("discount")} data-testid="add-discount-btn"><Gift className="w-4 h-4 mr-1" /> Discount</Button>}
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => window.print()} data-testid="ledger-print-btn"><Printer className="w-4 h-4 mr-1" /> Print</Button>
        </div>
      </div>
      <div className="hidden print:block mb-3"><div className="text-lg font-bold">Staff Cash Ledger · {emp.employee_name} ({emp.employee_code})</div><div className="text-xs">{fmtDate(range.from_date)} – {fmtDate(range.to_date)}</div></div>
      <Card className="p-4 rounded-2xl border-slate-200 mb-4 flex items-end gap-3 flex-wrap print:hidden">
        <div><Label className="text-xs">From</Label><Input type="date" className="h-10 rounded-xl mt-1" value={range.from_date} onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))} data-testid="ledger-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" className="h-10 rounded-xl mt-1" value={range.to_date} onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))} data-testid="ledger-to" /></div>
        <Button variant="outline" className="h-10 rounded-xl" onClick={() => setRange({ from_date: "", to_date: istToday() })} data-testid="ledger-all-time">All time</Button>
        <div className="ml-auto text-right"><div className="text-[10px] uppercase text-slate-500">Current balance</div><div className="text-xl"><NetBadge v={data?.closing_balance ?? 0} testId="ledger-current-balance" /></div></div>
      </Card>
      {data && <div className="mb-4"><LedgerTotals t={data.totals} /></div>}
      <Card className="rounded-2xl border-slate-200 overflow-hidden"><div className="overflow-x-auto">{data ? <LedgerTable data={data} onDelete={user?.role === "admin" ? del : undefined} /> : <div className="py-8 text-center text-slate-500">Loading…</div>}</div></Card>

      <Dialog open={!!dlg} onOpenChange={(v) => !v && setDlg(null)}>
        <DialogContent data-testid="ledger-entry-dialog">
          <DialogHeader><DialogTitle>{dlg === "advance" ? "Advance given to" : dlg === "return" ? "Money returned by" : "Discount / waiver for"} {emp.employee_name}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Amount (₹)</Label><Input type="number" step="0.01" inputMode="decimal" className="h-11 rounded-xl mt-1 font-mono-num" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="entry-amount" autoFocus /></div>
              <div><Label className="text-xs">Date</Label><Input type="date" className="h-11 rounded-xl mt-1" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="entry-date" /></div>
            </div>
            {dlg !== "discount" && (
              <div><Label className="text-xs">Mode</Label>
                <div className="flex gap-2 mt-1" data-testid="entry-mode">
                  {["cash", "upi"].map((m) => <button key={m} type="button" onClick={() => setForm({ ...form, mode: m })} className={`flex-1 h-11 rounded-xl border text-sm font-semibold uppercase ${form.mode === m ? "bg-[color:var(--bpcl-blue)] text-white border-transparent" : "border-slate-200 bg-white"}`} data-testid={`mode-${m}`}>{m}</button>)}
                </div>
              </div>
            )}
            <div><Label className="text-xs">Note</Label><Input className="h-11 rounded-xl mt-1" placeholder={dlg === "advance" ? "e.g. Salary advance for May" : "optional"} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="entry-note" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setDlg(null)}>Cancel</Button>
            <Button className="rounded-xl bg-[color:var(--bpcl-blue)] hover:bg-[color:var(--bpcl-blue-dark)]" onClick={save} disabled={!form.amount || Number(form.amount) <= 0} data-testid="entry-save-btn"><Plus className="w-4 h-4 mr-1" /> Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
